import * as React from "react";
import {
  IDropdownOption,
  Stack,
  DefaultButton,
  PrimaryButton,
  DatePicker,
  DayOfWeek,
  Dropdown,
  Checkbox,
  Breadcrumb,
  Callout,
  DirectionalHint,
} from "office-ui-fabric-react";
import {
  IDataCenter,
  ISearchHt,
  IHTReading,
  IReadingStatus,
} from "../../../vm";
import { ToastContext } from "../../common/ToastProvider";
import { ConfirmDialogContext } from "../../common/ConfirmDialogProvider";
import { useParams, useHistory } from "react-router-dom";
import {
  getADataCenter,
  getPowerComponents,
} from "../../../services/datacenter/DatacenterService";
import moment from "moment";
import { getHtReadings } from "../../../services/infrastructure/HtService";
import { updateHtReadingStatus } from "../../../services/infrastructure/HtService";
import { Loading } from "../../../services/Loading";
import {
  childrenGap,
  EVEN_SHIFT,
  UPS_STATUS,
  READ_AT_TIME_FORMAT,
  childrenGap8,
} from "../../common/Constants";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiChevronRight, mdiDotsVertical } from "@mdi/js";
import NoDataTile from "../../common/NoDataTile";
import {
  getThreshold,
  isInThreshold,
  isValueLessThanThreshold,
} from "../../../services/ThresholdService";
import ManageHtPanelDialog from "./ManageHtPanelDialog";
import ManageAllRejectDialog from "../../common/ManageAllRejectDialog";
import ShowThresholdIcon from "../../common/ShowThresholdIcon";

export interface HtPanelMainProps { }

const HtPanelMain: React.FC<HtPanelMainProps> = () => {
  //state
  const [state, setState] = React.useState({
    isLoading: false,
    isDatacenterFound: false,
    isHtLoaded: false,
    allHtDropdownList: [],
    datacenter: undefined,
    searchObj: { datacenterId: "", date: new Date(), htId: "" },
    htReading: [],
    isAllChecked: false,
    selectedItems: [],
    manageHtDialog: {
      isOpen: false,
      index: undefined,
      data: undefined,
    },
    threshold: undefined,
    manageRejectDialog: {
      isOpen: false,
      ids: [],
      remarks: "",
    },
    manageActionsCallout: {
      isOpen: false,
      element: undefined,
      data: undefined,
    },
  } as {
    isLoading: boolean;
    isDatacenterFound: boolean;
    isHtLoaded: boolean;
    allHtDropdownList: IDropdownOption[];
    datacenter?: IDataCenter;
    searchObj: ISearchHt;
    htReading: IHTReading[];
    isAllChecked: boolean;
    selectedItems: string[];
    manageHtDialog: {
      isOpen: boolean;
      index?: number;
      data?: IHTReading;
    };
    threshold?: any;
    manageRejectDialog: {
      isOpen: boolean;
      ids: string[];
      remarks?: string;
    };
    manageActionsCallout: {
      isOpen: boolean;
      element: any;
      data?: IHTReading;
    };
  });

  //hooks
  const { showToast } = React.useContext(ToastContext);
  const { showConfirmDialog } = React.useContext(ConfirmDialogContext);
  const params: any = useParams();
  const history = useHistory();

  React.useEffect(() => {
    const asyncFunc = async () => {
      if (params.dataCenterId) {
        getDatacenter(params.dataCenterId);
      }
    };
    asyncFunc();
  }, []);

  const getDatacenter = async (datacenterId: string) => {
    setState({ ...state, isLoading: true });
    let result = await getADataCenter(datacenterId);
    if (result?.success) {
      setState({
        ...state,
        datacenter: result.data,
        isDatacenterFound: true,
        isLoading: false,
      });

      getThresholdData(result.data);
    } else {
      showToast(result?.message || "Error while getting datacenter", "error");
      setState({ ...state, isDatacenterFound: false, isLoading: false });
    }
  };

  const getThresholdData = async (datacenter: IDataCenter) => {
    setState({ ...state, isLoading: true });
    let result = await getThreshold("ht-panel");
    if (result?.success) {
      getPowerComponentsList(datacenter, result.data.threshold);
    } else {
      showToast(result?.message || "Error while getting datacenter", "error");
      setState({ ...state, isDatacenterFound: false, isLoading: false });
    }
  };

  const getPowerComponentsList = async (
    datacenter: IDataCenter,
    threshold?: any
  ) => {
    setState({ ...state, isLoading: true });
    let result = await getPowerComponents({
      datacenterId: params.dataCenterId,
      types: ["ht-panel"],
    });
    if (result?.success) {
      let options: IDropdownOption[] = [];
      result.data.forEach((ele) => {
        options.push({ key: ele._id, text: ele.name });
      });
      let obj = {
        ...state,
        isLoading: false,
        datacenter,
        isDatacenterFound: true,
        searchObj: {
          datacenterId: params.dataCenterId,
          date: new Date(),
          htId: options.length > 0 ? (options[0].key as string) : "",
        },
        allHtDropdownList: options,
        threshold,
      };
      setState(obj);
      getHtReadingsData(obj);
    } else {
      showToast(result?.message || "Error while getting ht list", "error");
      setState({ ...state, isLoading: false });
    }
  };

  const getHtReadingsData = async (paramState?: any) => {
    let localState = { ...state };
    if (paramState != null) {
      localState = { ...paramState };
    }

    setState({ ...localState, isLoading: true });
    let obj = { ...localState.searchObj };
    obj.date = moment(obj.date).format("YYYY-MM-DD");
    let result = await getHtReadings(obj);
    if (result?.success) {
      setState({
        ...localState,
        htReading: result.data,
        isLoading: false,
        isHtLoaded: true,
      });
    } else {
      showToast(result?.message || "Error while getting ht readings", "error");
      setState({ ...state, isLoading: false, isHtLoaded: false });
    }
  };

  const onSearch = async () => {
    if (!state.searchObj.htId) {
      showToast("Please select an ht panel before searching", "error");
      return;
    }
    getHtReadingsData();
  };

  const handleAcceptOrReject = (id: string, isAccept: boolean) => {
    showConfirmDialog(
      "Are You Sure",
      `Do you want to ${isAccept ? "Accept" : "Reject"} the ht-panel reading`,
      async () => {
        setState({
          ...state,
          isLoading: true,
          manageActionsCallout: {
            isOpen: false,
            element: undefined,
            data: undefined,
          },
        });
        let obj: IReadingStatus = {
          ids: [id],
          status: isAccept ? "approved" : "rejected",
        };
        let result = await updateHtReadingStatus(obj);
        if (result?.success) {
          let htReading = [...state.htReading];
          let index = htReading.findIndex((ele) => ele._id === id);
          if (index !== -1) {
            htReading[index].status = isAccept ? "approved" : "rejected";
          }
          let selectedItems = [...state.selectedItems];
          let indexToRemove = selectedItems.findIndex((ele) => ele === id);
          if (index !== -1) {
            selectedItems.splice(indexToRemove, 1);
          }
          showToast(
            `Ht-Panel Reading ${isAccept ? "approved" : "rejected"
            } successfully`,
            "success"
          );
          setState({
            ...state,
            htReading,
            selectedItems,
            isLoading: false,
            manageActionsCallout: {
              isOpen: false,
              element: undefined,
              data: undefined,
            },
          });
        } else {
          showToast(
            result?.message ||
            `An error occurred while ${isAccept ? "approving" : "rejecting"
            } a ht-panel reading`,
            "error"
          );
          setState({
            ...state,
            isLoading: false,
            manageActionsCallout: {
              isOpen: false,
              element: undefined,
              data: undefined,
            },
          });
        }
      }
    );
  };

  const handleAcceptOrRejectSelected = (isAccept: boolean) => {
    if (isAccept) {
      showConfirmDialog(
        "Are You Sure",
        `Do you want to ${isAccept ? "Accept" : "Reject"
        } selected ht-panel reading`,
        async () => {
          let selectedItems = [...state.selectedItems];
          let htReading = [...state.htReading];
          setState({
            ...state,
            isLoading: true,
          });
          let obj: IReadingStatus = {
            ids: selectedItems,
            status: isAccept ? "approved" : "rejected",
          };
          let result = await updateHtReadingStatus(obj);
          if (result?.success) {
            selectedItems.forEach((item) => {
              let index = htReading.findIndex((ele) => ele._id === item);
              if (index !== -1) {
                htReading[index].status = isAccept ? "approved" : "rejected";
              }
            });
            showToast(
              `Ht-Panel Reading ${isAccept ? "approved" : "rejected"
              } successfully`,
              "success"
            );
            setState({
              ...state,
              htReading,
              selectedItems: [],
              isLoading: false,
            });
          } else {
            showToast(
              result?.message ||
              `An error occurred while ${isAccept ? "approving" : "rejecting"
              } a ht-panel reading`,
              "error"
            );
            setState({
              ...state,
              isLoading: false,
            });
          }
        }
      );
    } else {
      let selectedItems = [...state.selectedItems];
      handleRejectAllOpenDialog(selectedItems);
    }
  };

  const onSelectAll = (isAllChecked: boolean) => {
    let selectedItems: string[] = [];
    if (isAllChecked) {
      state.htReading.forEach((ele) => {
        if (ele.status === "submitted") {
          selectedItems.push(ele._id as string);
        }
      });
      setState({ ...state, isAllChecked: true, selectedItems });
    } else {
      setState({ ...state, isAllChecked: false, selectedItems });
    }
  };

  const onIndividualSelect = (id: string) => {
    let selectedItems = [...state.selectedItems];
    let index = selectedItems.findIndex((e) => e === id);
    if (index === -1) {
      selectedItems.push(id);
    } else {
      selectedItems.splice(index, 1);
    }
    let isAllChecked = true;
    state.htReading.forEach((ele) => {
      if (
        ele.status === "submitted" &&
        selectedItems.includes(ele._id as string) === false
      ) {
        isAllChecked = false;
      }
    });
    setState({ ...state, selectedItems, isAllChecked });
  };

  const getMinValue = (property: string) => {
    let a = property.split(".");
    if (state.htReading.length > 0) {
      let list = state.htReading.map((ele: any) => {
        if (a[1]) {
          return ele[a[0]] ? ele[a[0]][a[1]] : "";
        } else {
          return ele[a[0]];
        }
      });
      return Math.min(...list);
    } else {
      return "";
    }
  };
  const getMaxValue = (property: string) => {
    let a = property.split(".");
    if (state.htReading.length > 0) {
      let list = state.htReading.map((ele: any) => {
        if (a[1]) {
          return ele[a[0]] ? ele[a[0]][a[1]] : "";
        } else {
          return ele[a[0]];
        }
      });
      return Math.max(...list);
    } else {
      return "";
    }
  };

  const handleDialogOpen = (isOpen: boolean, id?: string) => {
    let htReading = undefined;
    let index = undefined;
    if (id !== undefined) {
      index = state.htReading.findIndex((ele) => ele._id === id);
      htReading = { ...state.htReading[index] };
    }
    setState({
      ...state,
      manageHtDialog: {
        isOpen: isOpen,
        index: index,
        data: htReading,
      },
    });
  };

  const handleDialogClose = (data: IHTReading) => {
    let htReading = [...state.htReading];
    if (data) {
      if (state.manageHtDialog.index !== undefined) {
        htReading[state.manageHtDialog.index] = data;
      }
    }
    setState({
      ...state,
      manageHtDialog: {
        isOpen: false,
        index: undefined,
        data: undefined,
      },
      htReading,
    });
  };

  const handleRejectAllOpenDialog = (ids: string[]) => {
    setState({
      ...state,
      manageRejectDialog: {
        isOpen: true,
        ids: ids || [],
        remarks: "",
      },
    });
  };
  const handleRejectOpenDialog = (id: string, remarks?: string) => {
    setState({
      ...state,
      manageRejectDialog: {
        isOpen: true,
        ids: [id],
        remarks: remarks || "",
      },
    });
  };

  const handleRejectCloseDialog = async (
    isSuccess: boolean,
    remarks?: string
  ) => {
    let htReading = [...state.htReading];
    let selectedItems: string[] = state.manageRejectDialog.ids;
    if (isSuccess) {
      setState({
        ...state,
        isLoading: true,
      });
      let obj: IReadingStatus = {
        ids: selectedItems,
        status: "rejected",
        remarks: remarks || "",
      };
      let result = await updateHtReadingStatus(obj);
      if (result?.success) {
        selectedItems.forEach((item) => {
          let index = htReading.findIndex((ele) => ele._id === item);
          if (index !== -1) {
            htReading[index].status = "rejected";
            htReading[index].remarks = remarks || "";
          }
        });
        showToast(`Ht-Panel Reading rejected successfully`, "success");
        setState({
          ...state,
          htReading,
          selectedItems: [],
          manageRejectDialog: {
            isOpen: false,
            ids: [],
            remarks: undefined,
          },
          isLoading: false,
        });
      } else {
        showToast(
          result?.message ||
          `An error occurred while rejecting a ht-panel reading`,
          "error"
        );
        setState({
          ...state,
          manageRejectDialog: {
            isOpen: false,
            ids: [],
            remarks: undefined,
          },
          isLoading: false,
        });
      }
    } else {
      setState({
        ...state,
        manageRejectDialog: {
          isOpen: false,
          ids: [],
          remarks: undefined,
        },
        htReading,
      });
    }
  };

  const handleLoading = () => {
    setState({
      ...state,
      isLoading: !state.isLoading,
    });
  };

  const onNextOrPrevFunc = (isPrev: boolean) => {
    let localState = { ...state };

    let currentSelectedHt = localState.searchObj.htId;
    let currentHtList = localState.allHtDropdownList;

    let currentIndex = currentHtList.findIndex(
      (d) => d.key === currentSelectedHt
    );

    if (isPrev) {
      if (currentIndex !== 0) {
        localState = {
          ...localState,
          searchObj: {
            ...localState.searchObj,
            htId: currentHtList[currentIndex - 1].key as string,
          },
        };
      }
    } else {
      if (currentIndex !== currentHtList.length - 1) {
        localState = {
          ...localState,
          searchObj: {
            ...localState.searchObj,
            htId: currentHtList[currentIndex + 1].key as string,
          },
        };
      }
    }
    getHtReadingsData(localState);
  };

  const handleCallOutOpenClose = (
    isOpen: boolean,
    ele?: React.MouseEvent<HTMLSpanElement>,
    data?: IHTReading
  ) => {
    setState({
      ...state,
      manageActionsCallout: {
        isOpen,
        element: isOpen ? ele?.currentTarget : undefined,
        data,
      },
    });
  };

  return (
    <React.Fragment>
      {state.isLoading && <Loading />}
      {state.isDatacenterFound ? (
        <React.Fragment>
          <Stack>
            <Stack.Item>
              <Stack.Item>
                {/* <Text variant="xLarge">
                  HT-Panel Readings -{" "}
                  {state.datacenter && state.datacenter.name}{" "}
                  {state.datacenter && state.datacenter.location && " - "}
                  {state.datacenter && state.datacenter.location}
                </Text> */}
                <Breadcrumb
                  items={[
                    {
                      text: state.datacenter
                        ? state.datacenter.name
                        : "Datacenter",
                      key: "datacenter",
                    },
                    {
                      text: "Infrastructures",
                      key: "infrastructure",
                      onClick: () => {
                        history.goBack();
                      },
                    },
                    {
                      text: "Ht-Panel Readings",
                      key: "ht-readings",
                      isCurrentItem: true,
                    },
                  ]}
                />
              </Stack.Item>
            </Stack.Item>
          </Stack>
          <br />
          <Stack horizontal horizontalAlign="start">
            <Stack.Item>
              <Stack horizontal wrap tokens={childrenGap}>
                <Stack.Item>
                  <Dropdown
                    placeholder="Select a HT-Panel"
                    // label="Role"
                    selectedKey={state.searchObj.htId}
                    styles={{ dropdown: { width: 200 } }}
                    options={state.allHtDropdownList}
                    onChange={(e, v: IDropdownOption | undefined) => {
                      if (v) {
                        setState({
                          ...state,
                          searchObj: {
                            ...state.searchObj,
                            htId: v.key as string,
                          },
                        });
                      }
                    }}
                  />
                </Stack.Item>
                <Stack.Item>
                  <DatePicker
                    firstDayOfWeek={DayOfWeek.Sunday}
                    showMonthPickerAsOverlay={true}
                    value={new Date(state.searchObj.date)}
                    showGoToToday={false}
                    onSelectDate={(date: Date | null | undefined) =>
                      setState({
                        ...state,
                        searchObj: { ...state.searchObj, date: date as Date },
                      })
                    }
                    placeholder="Select a date..."
                    ariaLabel="Select a date"
                    styles={{ root: { width: 200 } }}
                  />
                </Stack.Item>
                <Stack.Item>
                  <PrimaryButton text="Search" onClick={() => onSearch()} />
                </Stack.Item>
              </Stack>
            </Stack.Item>
            {/* <Stack.Item>
              <DefaultButton
                text="Accept"
                disabled={state.selectedItems.length === 0}
                onClick={() => handleAcceptOrRejectSelected(true)}
              />
              &nbsp;&nbsp;
              <DefaultButton
                text="Reject"
                disabled={state.selectedItems.length === 0}
                onClick={() => handleAcceptOrRejectSelected(false)}
              />
            </Stack.Item> */}
          </Stack>
          <br />
          <Stack
            horizontal
            horizontalAlign="space-between"
            tokens={childrenGap}
            wrap
          >
            <Stack.Item styles={{ root: { marginLeft: 0 } }}>
              <span
                onClick={
                  state.allHtDropdownList.length < 1 ||
                    state.allHtDropdownList[0]?.key === state.searchObj.htId
                    ? undefined
                    : () => onNextOrPrevFunc(true)
                }
                className={
                  state.allHtDropdownList.length < 1 ||
                    state.allHtDropdownList[0]?.key === state.searchObj.htId
                    ? "btn-disabled"
                    : "btn-enabled pointer"
                }
              >
                <Icon
                  path={mdiChevronLeft}
                  size={0.9}
                  className="btnIcon"
                  style={{
                    marginBottom: -6,
                    marginRight: -8,
                    color: "#135c8a",
                  }}
                />
                <DefaultButton
                  text="Previous Panel"
                  className="borderless-btn"
                // disabled={
                //   state.allHtDropdownList[0].key === state.searchObj.htId
                // }
                // onClick={() => {
                //   onNextOrPrevFunc(true);
                // }}
                />
              </span>
              <span className="nxt-prv-btn-border"></span>
              <span
                onClick={
                  state.allHtDropdownList.length < 1 ||
                    state.allHtDropdownList[state.allHtDropdownList.length - 1]
                      ?.key === state.searchObj.htId
                    ? undefined
                    : () => onNextOrPrevFunc(false)
                }
                className={
                  state.allHtDropdownList.length < 1 ||
                    state.allHtDropdownList[state.allHtDropdownList.length - 1]
                      ?.key === state.searchObj.htId
                    ? "btn-disabled"
                    : "btn-enabled pointer"
                }
              >
                <DefaultButton
                  text="Next Panel"
                  // disabled={
                  //   state.allHtDropdownList[state.allHtDropdownList.length - 1]
                  //     .key === state.searchObj.htId
                  // }
                  // onClick={() => {
                  //   onNextOrPrevFunc(false);
                  // }}
                  className="borderless-btn"
                />
                <Icon
                  path={mdiChevronRight}
                  size={0.9}
                  className="btnIcon"
                  style={{
                    marginBottom: -6,
                    marginLeft: -8,
                    color: "#135c8a",
                  }}
                />
              </span>
            </Stack.Item>
            <Stack.Item>
              <DefaultButton
                text="Accept"
                className="mr-8 custom-secondary-btn"
                disabled={state.selectedItems.length === 0}
                onClick={() => handleAcceptOrRejectSelected(true)}
              />
              <DefaultButton
                text="Reject"
                disabled={state.selectedItems.length === 0}
                onClick={() => handleAcceptOrRejectSelected(false)}
                className="custom-secondary-btn"
              />
            </Stack.Item>
          </Stack>
          {/* <Stack horizontal horizontalAlign="end" wrap>
            <Stack.Item>
              <DefaultButton
                text="Accept"
                className="mr-8"
                disabled={state.selectedItems.length === 0}
                onClick={() => handleAcceptOrRejectSelected(true)}
              />
            </Stack.Item>
            <Stack.Item>
              <DefaultButton
                text="Reject"
                disabled={state.selectedItems.length === 0}
                onClick={() => handleAcceptOrRejectSelected(false)}
              />
            </Stack.Item>
          </Stack> */}
          <br />
          {state.isHtLoaded && (
            <Stack>
              <div className="office-table bg-white">
                <table className="ms-Table bordered-table w-100">
                  <thead className="html-table-thead">
                    <tr>
                      <th rowSpan={2}>
                        <Checkbox
                          checked={state.isAllChecked}
                          onChange={(eve, checked) =>
                            onSelectAll(checked as boolean)
                          }
                        />
                      </th>
                      <th rowSpan={2}>Time (Hrs)</th>
                      <th rowSpan={2}>System</th>
                      <th colSpan={3}>HT VOLT</th>
                      <th colSpan={3}>HT AMPS</th>
                      <th rowSpan={2}>FREQ</th>
                      <th rowSpan={2}>KWH</th>
                      <th rowSpan={2}>KVAH</th>
                      <th colSpan={2}>Demand</th>
                      <th colSpan={2}>PF</th>
                      <th rowSpan={2}>Shift Tech Sign</th>
                      <th rowSpan={2}>Status</th>
                      <th rowSpan={2}>Remarks</th>
                      <th rowSpan={2}>Read At</th>
                      <th rowSpan={2}>Actions</th>
                    </tr>
                    <tr>
                      <th>RY</th>
                      <th>YB</th>
                      <th>BR</th>
                      <th>R</th>
                      <th>Y</th>
                      <th>B</th>
                      <th>PRES</th>
                      <th>CUMU</th>
                      <th>PRES</th>
                      <th>CUMU</th>
                    </tr>
                  </thead>
                  <tbody>
                    {EVEN_SHIFT.map((ele, index) => {
                      let item = state.htReading.find(
                        (e) => e.shiftTime === ele
                      );
                      return (
                        <tr key={index}>
                          <th>
                            <Checkbox
                              disabled={!item || item.status !== "submitted"}
                              checked={state.selectedItems.includes(
                                item?._id as string
                              )}
                              onChange={() =>
                                onIndividualSelect(item?._id as string)
                              }
                              className="ml-12"
                            />
                          </th>
                          <th> {ele} </th>
                          <td>
                            {item?.isSystemOn !== undefined
                              ? item?.isSystemOn === true
                                ? "On"
                                : "Off"
                              : ""}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.htVoltage?.RY || "",
                                "htVoltage.RY",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.htVoltage?.RY || ""}
                            {isInThreshold(
                              item?.htVoltage?.RY || "",
                              "htVoltage.RY",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.htVoltage?.RY || "",
                                      "htVoltage.RY",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.htVoltage?.YB || "",
                                "htVoltage.YB",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.htVoltage?.YB || ""}
                            {isInThreshold(
                              item?.htVoltage?.YB || "",
                              "htVoltage.YB",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.htVoltage?.YB || "",
                                      "htVoltage.YB",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.htVoltage?.BR || "",
                                "htVoltage.BR",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.htVoltage?.BR || ""}
                            {isInThreshold(
                              item?.htVoltage?.BR || "",
                              "htVoltage.BR",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.htVoltage?.BR || "",
                                      "htVoltage.BR",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.htAmps?.R || "",
                                "htAmps.R",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.htAmps?.R || ""}
                            {isInThreshold(
                              item?.htAmps?.R || "",
                              "htAmps.R",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.htAmps?.R || "",
                                      "htAmps.R",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.htAmps?.Y || "",
                                "htAmps.Y",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.htAmps?.Y || ""}
                            {isInThreshold(
                              item?.htAmps?.Y || "",
                              "htAmps.Y",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.htAmps?.Y || "",
                                      "htAmps.Y",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.htAmps?.B || "",
                                "htAmps.B",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.htAmps?.B || ""}
                            {isInThreshold(
                              item?.htAmps?.B || "",
                              "htAmps.B",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.htAmps?.B || "",
                                      "htAmps.B",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.frequency || "",
                                "frequency",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.frequency || ""}
                            {isInThreshold(
                              item?.frequency || "",
                              "frequency",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.frequency || "",
                                      "frequency",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.kwh || "",
                                "kwh",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.kwh || ""}
                            {isInThreshold(
                              item?.kwh || "",
                              "kwh",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.kwh || "",
                                      "kwh",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.kvah || "",
                                "kvah",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.kvah || ""}
                            {isInThreshold(
                              item?.kvah || "",
                              "kvah",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.kvah || "",
                                      "kvah",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.demand?.press || "",
                                "demand.press",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.demand?.press || ""}
                            {isInThreshold(
                              item?.demand?.press || "",
                              "demand.press",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.demand?.press || "",
                                      "demand.press",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.demand?.cumu || "",
                                "demand.cumu",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.demand?.cumu || ""}
                            {isInThreshold(
                              item?.demand?.cumu || "",
                              "demand.cumu",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.demand?.cumu || "",
                                      "demand.cumu",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.pf?.press || "",
                                "pf.press",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.pf?.press || ""}
                            {isInThreshold(
                              item?.pf?.press || "",
                              "pf.press",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.pf?.press || "",
                                      "pf.press",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.pf?.cumu || "",
                                "pf.cumu",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.pf?.cumu || ""}
                            {isInThreshold(
                              item?.pf?.cumu || "",
                              "pf.cumu",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.pf?.cumu || "",
                                      "pf.cumu",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td>{item?.recordedBy || ""}</td>
                          <td>
                            {item && item.status ? UPS_STATUS[item.status] : ""}
                          </td>
                          <td>{item?.remarks || ""}</td>
                          <td>
                            {item?.date
                              ? moment(item.date).format(READ_AT_TIME_FORMAT)
                              : ""}
                          </td>
                          <td align="center" className="no-wrap">
                            {item && item.status === "submitted" && (
                              <span
                                onClick={(ele) =>
                                  handleCallOutOpenClose(true, ele, item)
                                }
                              >
                                <Icon
                                  path={mdiDotsVertical}
                                  size={0.9}
                                  className="pointer"
                                  color="#135c8a"
                                />
                              </span>
                              // <React.Fragment>
                              //   <TooltipHost
                              //     content="Update"
                              //     calloutProps={{ gapSpace: 0 }}
                              //   >
                              //     <span
                              //       onClick={() =>
                              //         handleDialogOpen(
                              //           true,
                              //           item?._id as string
                              //         )
                              //       }
                              //     >
                              //       <Icon
                              //         path={mdiPencil}
                              //         size={0.7}
                              //         className="pointer"
                              //       />
                              //     </span>
                              //   </TooltipHost>
                              //   &nbsp;&nbsp;
                              //   <TooltipHost
                              //     content="Accept"
                              //     calloutProps={{ gapSpace: 0 }}
                              //   >
                              //     <span
                              //       onClick={() =>
                              //         handleAcceptOrReject(
                              //           item?._id as string,
                              //           true
                              //         )
                              //       }
                              //     >
                              //       <Icon
                              //         path={mdiCheck}
                              //         size={0.8}
                              //         className="pointer"
                              //         color="green"
                              //       />
                              //     </span>
                              //   </TooltipHost>
                              //   &nbsp;&nbsp;
                              //   <TooltipHost
                              //     content="Reject"
                              //     calloutProps={{ gapSpace: 0 }}
                              //   >
                              //     <span
                              //       onClick={() =>
                              //         handleRejectOpenDialog(
                              //           item?._id as string,
                              //           item?.remarks
                              //         )
                              //       }
                              //     >
                              //       <Icon
                              //         path={mdiClose}
                              //         size={0.8}
                              //         className="pointer"
                              //         color="red"
                              //       />
                              //     </span>
                              //   </TooltipHost>
                              // </React.Fragment>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td></td>
                      <th>Min Value</th>
                      <td></td>
                      <td>{getMinValue("htVoltage.RY")}</td>
                      <td>{getMinValue("htVoltage.YB")}</td>
                      <td>{getMinValue("htVoltage.BR")}</td>
                      <td>{getMinValue("htAmps.R")}</td>
                      <td>{getMinValue("htAmps.Y")}</td>
                      <td>{getMinValue("htAmps.B")}</td>
                      <td>{getMinValue("frequency")}</td>
                      <td>{getMinValue("kwh")}</td>
                      <td>{getMinValue("kvah")}</td>
                      <td>{getMinValue("demand.press")}</td>
                      <td>{getMinValue("demand.cumu")}</td>
                      <td>{getMinValue("pf.press")}</td>
                      <td>{getMinValue("pf.cumu")}</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                    <tr>
                      <td></td>
                      <th>Max Value</th>
                      <td></td>
                      <td>{getMaxValue("htVoltage.RY")}</td>
                      <td>{getMaxValue("htVoltage.YB")}</td>
                      <td>{getMaxValue("htVoltage.BR")}</td>
                      <td>{getMaxValue("htAmps.R")}</td>
                      <td>{getMaxValue("htAmps.Y")}</td>
                      <td>{getMaxValue("htAmps.B")}</td>
                      <td>{getMaxValue("frequency")}</td>
                      <td>{getMaxValue("kwh")}</td>
                      <td>{getMaxValue("kvah")}</td>
                      <td>{getMaxValue("demand.press")}</td>
                      <td>{getMaxValue("demand.cumu")}</td>
                      <td>{getMaxValue("pf.press")}</td>
                      <td>{getMaxValue("pf.cumu")}</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {/* <div className="next-prev-btn">
                <div>
                  <DefaultButton
                    text="Previous Panel"
                    className="mr-8"
                    disabled={
                      state.allHtDropdownList[0].key === state.searchObj.htId
                    }
                    onClick={() => {
                      onNextOrPrevFunc(true);
                    }}
                  />
                  <DefaultButton
                    text="Next Panel"
                    disabled={
                      state.allHtDropdownList[
                        state.allHtDropdownList.length - 1
                      ].key === state.searchObj.htId
                    }
                    onClick={() => {
                      onNextOrPrevFunc(false);
                    }}
                  />
                </div>
              </div> */}
            </Stack>
          )}
        </React.Fragment>
      ) : (
        <NoDataTile
          title="No Datacenter Found"
          description="Datacenter either deleted or not created"
        />
      )}
      {state.manageActionsCallout.isOpen && (
        <Callout
          styles={{ root: { width: 95, padding: 20 } }}
          role="alertdialog"
          ariaLabelledBy={
            state.manageActionsCallout && state.manageActionsCallout.data?._id
          }
          gapSpace={0}
          target={state.manageActionsCallout.element}
          directionalHint={DirectionalHint.leftCenter}
          onDismiss={() => handleCallOutOpenClose(false)}
        // setInitialFocus={true}
        >
          <Stack tokens={childrenGap8}>
            <Stack.Item className="pointer">
              <span
                onClick={() =>
                  handleDialogOpen(
                    true,
                    state.manageActionsCallout.data?._id as string
                  )
                }
                className="callout-options"
              >
                Edit
              </span>
            </Stack.Item>
            <Stack.Item className="pointer">
              <span
                onClick={() =>
                  handleAcceptOrReject(
                    state.manageActionsCallout.data?._id as string,
                    true
                  )
                }
                className="callout-options"
              >
                Accept
              </span>
            </Stack.Item>
            <Stack.Item className="pointer">
              <span
                onClick={() =>
                  handleRejectOpenDialog(
                    state.manageActionsCallout.data?._id as string,
                    state.manageActionsCallout.data?.remarks
                  )
                }
                className="callout-options"
              >
                Reject
              </span>
            </Stack.Item>
          </Stack>
        </Callout>
      )}
      {state.manageHtDialog.isOpen && (
        <ManageHtPanelDialog
          data={state.manageHtDialog.data}
          handleLoading={handleLoading}
          handleDialogClose={(data: IHTReading) => handleDialogClose(data)}
          threshold={state.threshold}
        />
      )}
      {state.manageRejectDialog.isOpen && (
        <ManageAllRejectDialog
          handleRejectCloseDialog={(isSuccess: boolean, remarks?: string) =>
            handleRejectCloseDialog(isSuccess, remarks)
          }
          remarks={state.manageRejectDialog.remarks}
          subText="ht-panel reading"
        />
      )}
    </React.Fragment>
  );
};

export default HtPanelMain;
